import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import {
  AlertCircle,
  Clock,
  Coffee,
  DollarSign,
  Package,
  ShoppingCart,
  TrendingUp,
  Utensils,
} from 'lucide-react'
import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { PageLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { toIdr } from '@/libs/utils/format'

// import { reportDashboardSummaryQuery } from '@/features/report/api'

export const Route = createFileRoute('/_app/')({
  component: Component,
})

function Component() {
  return (
    <PageLayout hideBreadcrumbs>
      <HomeDashboardPage />
    </PageLayout>
  )
}

interface Outlet {
  id: string
  name: string
  icon: React.ReactNode
}

const OUTLETS: Outlet[] = [
  { id: 'ikki_coffee', name: 'Ikki Coffee', icon: <Coffee className='h-5 w-5' /> },
  { id: 'ikki_resto', name: 'Ikki Resto', icon: <Utensils className='h-5 w-5' /> },
]

const mockHomeData = {
  ikki_coffee: {
    todayRevenue: 5500000,
    todayProfit: 1850000,
    todayTransactions: 105,
    openCashRegister: { amount: 500000, time: '08:00' },
    topProducts: [
      { name: 'Latte', sales: 42, revenue: 1680000 },
      { name: 'Cappuccino', sales: 38, revenue: 1520000 },
      { name: 'Americano', sales: 25, revenue: 750000 },
    ],
    lowStock: [
      { name: 'Biji Kopi Arabica', stock: 3, unit: 'kg' },
      { name: 'Susu UHT', stock: 8, unit: 'liter' },
    ],
    hourlySales: [
      { hour: '08:00', revenue: 320000 },
      { hour: '09:00', revenue: 580000 },
      { hour: '10:00', revenue: 720000 },
      { hour: '11:00', revenue: 890000 },
      { hour: '12:00', revenue: 1100000 },
      { hour: '13:00', revenue: 950000 },
      { hour: '14:00', revenue: 780000 },
      { hour: '15:00', revenue: 650000 },
    ],
    dailyTrend: [
      { day: 'Sen', revenue: 4800000 },
      { day: 'Sel', revenue: 5200000 },
      { day: 'Rab', revenue: 4900000 },
      { day: 'Kam', revenue: 5500000 },
      { day: 'Jum', revenue: 6200000 },
      { day: 'Sab', revenue: 6800000 },
      { day: 'Min', revenue: 5500000 },
    ],
  },
  ikki_resto: {
    todayRevenue: 7500000,
    todayProfit: 2450000,
    todayTransactions: 142,
    openCashRegister: { amount: 1000000, time: '10:00' },
    topProducts: [
      { name: 'Nasi Goreng Spesial', sales: 58, revenue: 2320000 },
      { name: 'Ayam Bakar', sales: 45, revenue: 1800000 },
      { name: 'Mie Goreng', sales: 39, revenue: 1170000 },
    ],
    lowStock: [
      { name: 'Beras Premium', stock: 5, unit: 'kg' },
      { name: 'Ayam Potong', stock: 12, unit: 'kg' },
    ],
    hourlySales: [
      { hour: '11:00', revenue: 480000 },
      { hour: '12:00', revenue: 980000 },
      { hour: '13:00', revenue: 1350000 },
      { hour: '14:00', revenue: 1120000 },
      { hour: '15:00', revenue: 890000 },
      { hour: '16:00', revenue: 720000 },
      { hour: '17:00', revenue: 950000 },
      { hour: '18:00', revenue: 1080000 },
    ],
    dailyTrend: [
      { day: 'Sen', revenue: 6800000 },
      { day: 'Sel', revenue: 7200000 },
      { day: 'Rab', revenue: 6900000 },
      { day: 'Kam', revenue: 7500000 },
      { day: 'Jum', revenue: 8200000 },
      { day: 'Sab', revenue: 8800000 },
      { day: 'Min', revenue: 7500000 },
    ],
  },
}

function HomeDashboardPage() {
  const [outletId, setOutletId] = useState('ikki_coffee')
  // @ts-expect-error
  const data = mockHomeData[outletId]
  const outlet = OUTLETS.find(o => o.id === outletId)!

  const revenueChartConfig = {
    revenue: { label: 'Pendapatan', color: 'hsl(var(--chart-1))' },
  } satisfies ChartConfig

  const dailyChartConfig = {
    revenue: { label: 'Pendapatan', color: 'hsl(var(--chart-2))' },
  } satisfies ChartConfig

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div
        className={`
          flex flex-col items-start justify-between gap-4
          md:flex-row md:items-center
        `}
      >
        <div className='flex items-center gap-3'>
          <div
            className={`
              rounded-lg bg-blue-100 p-3
              dark:bg-blue-900
            `}
          >
            {outlet.icon}
          </div>
          <div>
            <h1
              className={`
                text-3xl font-bold text-gray-900
                dark:text-white
              `}
            >
              Selamat Datang di {outlet.name}
            </h1>
            <p
              className={`
                text-sm text-gray-500
                dark:text-gray-400
              `}
            >
              {format(new Date(), 'EEEE, d MMMM yyyy', { locale: id })}
            </p>
          </div>
        </div>

        <div className='flex gap-2'>
          {OUTLETS.map(o => (
            <Button
              key={o.id}
              variant={outletId === o.id ? 'default' : 'outline'}
              size='sm'
              onClick={() => setOutletId(o.id)}
              className='flex items-center gap-2'
            >
              {o.icon}
              {o.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Today Summary */}
      <div
        className={`
          grid grid-cols-1 gap-4
          md:grid-cols-2
          lg:grid-cols-4
        `}
      >
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Pendapatan Hari Ini</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{toIdr(data.todayRevenue)}</div>
            <p className='flex items-center gap-1 text-xs text-muted-foreground'>
              <TrendingUp className='h-3 w-3 text-green-600' />
              +8.2% dari kemarin
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Laba Hari Ini</CardTitle>
            <TrendingUp className='h-4 w-4 text-green-600' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold text-green-600'>{toIdr(data.todayProfit)}</div>
            <p className='text-xs text-muted-foreground'>
              Margin: {((data.todayProfit / data.todayRevenue) * 100).toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Transaksi Hari Ini</CardTitle>
            <ShoppingCart className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{data.todayTransactions}</div>
            <p className='text-xs text-muted-foreground'>
              Rata-rata: {toIdr(Math.round(data.todayRevenue / data.todayTransactions))}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Kas Dibuka</CardTitle>
            <Clock className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-lg font-semibold'>{toIdr(data.openCashRegister.amount)}</div>
            <p className='text-xs text-muted-foreground'>Jam {data.openCashRegister.time}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts & Alerts */}
      <Tabs defaultValue='hourly' className='space-y-4'>
        <TabsList
          className={`
            grid w-full grid-cols-3
            lg:inline-flex lg:w-auto
          `}
        >
          <TabsTrigger value='hourly'>Penjualan per Jam</TabsTrigger>
          <TabsTrigger value='weekly'>7 Hari Terakhir</TabsTrigger>
          <TabsTrigger value='alerts'>Peringatan</TabsTrigger>
        </TabsList>

        <TabsContent value='hourly'>
          <Card>
            <CardHeader>
              <CardTitle>Penjualan per Jam Hari Ini</CardTitle>
              <CardDescription>{format(new Date(), 'd MMMM yyyy', { locale: id })}</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={revenueChartConfig} className='h-64'>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={data.hourlySales}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='hour' />
                    <YAxis tickFormatter={v => `Rp${(v / 1000000).toFixed(1)}jt`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type='monotone'
                      dataKey='revenue'
                      stroke='var(--color-revenue)'
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-revenue)' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='weekly'>
          <Card>
            <CardHeader>
              <CardTitle>Pendapatan 7 Hari Terakhir</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={dailyChartConfig} className='h-64'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={data.dailyTrend}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='day' />
                    <YAxis tickFormatter={v => `Rp${(v / 1000000).toFixed(0)}jt`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey='revenue' fill='var(--color-revenue)' radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='alerts' className='space-y-4'>
          <div
            className={`
              grid grid-cols-1 gap-4
              md:grid-cols-2
            `}
          >
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2 text-orange-600'>
                  <AlertCircle className='h-5 w-5' />
                  Stok Rendah
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {data.lowStock.map((item, i) => (
                    <div
                      key={i}
                      className={`
                        flex items-center justify-between rounded bg-orange-50 p-2
                        dark:bg-orange-900/20
                      `}
                    >
                      <span className='font-medium'>{item.name}</span>
                      <Badge variant='destructive'>
                        {item.stock} {item.unit}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2 text-blue-600'>
                  <Package className='h-5 w-5' />
                  Produk Terlaris Hari Ini
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-2'>
                  {data.topProducts.map((p, i) => (
                    <div key={i} className='flex items-center justify-between'>
                      <div>
                        <p className='font-medium'>{p.name}</p>
                        <p className='text-xs text-muted-foreground'>{p.sales} terjual</p>
                      </div>
                      <span className='font-mono text-sm'>{toIdr(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// function Component() {
//   const [date] = useState(startOfDay(new Date()))
//   // const { outletId } = useAuth((s) => s.outlet)
//   // const { data, isLoading } = useQuery(
//   // 	reportDashboardSummaryQuery({ outletId, date })
//   // )

//   const isLoading = true
//   const data = {}

//   return (
//     <PageLayout>
//       {/* <PageLayout.Header title='Dashboard' /> */}
//       <PageCard.Root>
//         <div className='flex items-center justify-between px-6 py-4'>
//           <div className='flex flex-col gap-2'>
//             <Heading level='h2'>Penjualan</Heading>
//             <div className='flex items-center gap-2'>
//               <Calendar />
//               <Text className='text-ui-fg-subtle' size='small'>
//                 {dateId(date)}
//               </Text>
//             </div>
//           </div>
//         </div>
//         <div
//           className={`
//             flex flex-col gap-y-6 px-6 py-4
//             md:flex-row
//           `}
//         >
//           <div
//             className={`
//               flex flex-col items-start justify-center border-r-0 border-b pb-5
//               md:items-center md:border-r md:border-b-0 md:pr-5
//             `}
//           >
//             <div className='max-w-full min-w-72'>
//               <Text className='font-semibold'>Total Penjualan</Text>
//               <Text className='mt-1 text-2xl font-bold text-ui-tag-green-text'>
//                 {isLoading ? <Skeleton /> : toIdr(data?.summary?.net || 0)}
//               </Text>
//             </div>
//           </div>
//           <div
//             className={`
//               grid grid-cols-2 gap-4 pl-0
//               md:pl-4
//             `}
//           >
//             <CardStat label='Laba Kotor' value={toIdr(data?.summary?.gross || 0)} />
//             <CardStat label='Penjualan Kotor' value={toIdr(data?.summary?.net || 0)} />
//             <Divider className='col-span-full' />
//             <CardStat label='Produk terjual' value={data?.summary?.productSales || 0} />
//             <CardStat label='Transaksi' value={data?.summary?.sales || 0} />
//           </div>
//         </div>
//         <div className='flex h-40 items-center justify-center'>TODO: DATA CHART</div>
//       </PageCard.Root>
//       <div className='grid grid-cols-3 gap-4'>
//         <PageCard.Root>
//           <PageCard.Header title='Produk Terlaris' />
//           <PageCard.Content>
//             <PageCard.ContentSection>
//               <div className='col-span-full'>{/* <SalesTable /> */}</div>
//             </PageCard.ContentSection>
//           </PageCard.Content>
//         </PageCard.Root>
//         <PageCard.Root>
//           <PageCard.Header title='Stok Teredah' />
//           <PageCard.Content>
//             <PageCard.ContentSection>
//               <div className='col-span-full'>{/* <SalesTable /> */}</div>
//             </PageCard.ContentSection>
//           </PageCard.Content>
//         </PageCard.Root>
//         <PageCard.Root>
//           <PageCard.Header title='Metode Pembayaran' />
//           <PageCard.Content>
//             <PageCard.ContentSection>
//               <div className='col-span-full'>{/* <SalesTable /> */}</div>
//             </PageCard.ContentSection>
//           </PageCard.Content>
//         </PageCard.Root>
//       </div>
//     </PageLayout>
//   )
// }

// interface CardStatProps {
//   label: string
//   value: string | number
// }

// function CardStat({ label, value }: CardStatProps) {
//   return (
//     <div
//       className={`
//         px-0
//         md:px-4
//       `}
//     >
//       <Text className='text-sm font-medium text-ui-fg-muted'>{label}</Text>
//       <Text className='text-ui-fg-on-emphasis text-base font-semibold'>{value}</Text>
//     </div>
//   )
// }
